import React from 'react'
import Chip from '@mui/material/Chip'
import type { ChipProps } from '@mui/material/Chip'
import { useTheme } from '@mui/material/styles'
import type { SxProps } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import type { ClockMetaResponse } from '../../api/showClock'
import { resolveClockItemColorStyles, resolveClockItemTypes } from '../../utils/showClock'

type InteractionProps = Pick<
  ChipProps,
  | 'onClick'
  | 'onKeyDown'
  | 'onKeyUp'
  | 'onFocus'
  | 'onBlur'
  | 'onMouseEnter'
  | 'onMouseLeave'
  | 'tabIndex'
  | 'role'
  | 'aria-label'
>

type Props = {
  typeKey: string
  label?: string
  size?: 'small' | 'medium' | 'large'
  variant?: 'filled' | 'outlined'
  sx?: SxProps<Theme>
  className?: string
} & InteractionProps

const TypeChipMetaContext = React.createContext<ClockMetaResponse | null>(null)

export const TypeChipProvider: React.FC<React.PropsWithChildren<{ meta: ClockMetaResponse | null }>> = ({
  meta,
  children,
}) => {
  return <TypeChipMetaContext.Provider value={meta}>{children}</TypeChipMetaContext.Provider>
}

const TypeChip: React.FC<Props> = ({
  typeKey,
  label,
  size = 'medium',
  variant = 'filled',
  sx,
  className,
  onClick,
  ...interactionProps
}) => {
  const theme = useTheme()
  const meta = React.useContext(TypeChipMetaContext)
  const itemTypes = React.useMemo(() => resolveClockItemTypes(meta), [meta])
  const resolvedMeta = React.useMemo(() => itemTypes.find((item) => item.key === typeKey) ?? null, [itemTypes, typeKey])
  const chipLabel = label ?? resolvedMeta?.label ?? typeKey
  const { backgroundColor, color } = resolveClockItemColorStyles(theme, resolvedMeta?.colorToken ?? '')
  const chipVariant: 'filled' | 'outlined' = variant === 'outlined' ? 'outlined' : 'filled'
  const chipSize: 'small' | 'medium' = size === 'small' ? 'small' : 'medium'

  const baseStyles: SxProps<Theme> = {
    borderRadius: '999px',
    height: size === 'large' ? 'auto' : undefined,
    cursor: typeof onClick === 'function' ? 'pointer' : undefined,
    ...(chipVariant === 'outlined'
      ? {
          backgroundColor: 'transparent',
          color,
          borderStyle: 'solid',
          borderWidth: 1,
          borderColor: color,
        }
      : {
          backgroundColor,
          color,
        }),
    '& .MuiChip-label': {
      fontWeight: size === 'large' ? 700 : 600,
      ...(size === 'large'
        ? { fontSize: '1.25rem', lineHeight: 1.1, px: 2, py: 0.85 }
        : size === 'small'
        ? { px: 0.75, py: 0.25 }
        : { px: 1.25, py: 0.375 }),
    },
  }

  const finalSx: SxProps<Theme> = Array.isArray(sx)
    ? [baseStyles, ...sx]
    : sx
    ? [baseStyles, sx]
    : baseStyles

  return (
    <Chip
      className={className}
      label={chipLabel}
      size={chipSize}
      variant={chipVariant}
      sx={finalSx}
      onClick={onClick}
      {...interactionProps}
    />
  )
}

export default TypeChip
